import { useEffect, useEffectEvent, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';

import {
    OverviewPageOffersDocument,
    IOverviewPageOffersQueryVariables,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';

export const useOfferOverviewList = (
    queryVariables: IOverviewPageOffersQueryVariables
) => {
    const { showApolloError } = useApolloError();
    const {
        loading,
        data,
        fetchMore: fetchMoreOffers,
        error,
    } = useQuery(OverviewPageOffersDocument, {
        variables: queryVariables,
        notifyOnNetworkStatusChange: true,
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const {
        count: offersCount,
        pageInfo,
        extraCategories: extraCategoryQueryFilters,
    } = data?.offers || {};

    const offersObject = data?.offers;
    const offers = useMemo(
        () =>
            offersObject?.edges?.map((edge) => edge?.node).filter(Boolean) ||
            [],
        [offersObject?.edges]
    );

    const hasAllResults = offers.length >= (offersCount || 0);
    const shouldFetchMore = !hasAllResults && pageInfo?.hasNextPage && !loading;

    const extraCategoryFilters = useMemo(
        () => extraCategoryQueryFilters?.filter(Boolean) || [],
        [extraCategoryQueryFilters]
    );

    return {
        offers,
        offersCount,
        loading,
        fetchMoreOffers,
        pageInfo,
        shouldFetchMore,
        extraCategoryQueryFilters: extraCategoryFilters,
    };
};
